import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
  advisoryNoteService,
  AdvisoryNote,
  AdvisoryNotePriority,
} from '@services/advisoryNoteService';
import { toDisplayError } from '../../utils/apiError';
import { formatDateTime } from '@utils/format';

interface AdvisoryNotesPanelProps {
  orgId: string;
  findingId: string;
}

const PRIORITY_COLOR: Record<AdvisoryNotePriority, 'error' | 'warning' | 'default' | 'info'> = {
  critical: 'error',
  high: 'warning',
  normal: 'default',
  low: 'info',
};

/**
 * REV-217: inline advisory-notes panel. Lists existing notes chronologically
 * and offers an add-note form. Delete is available on the caller's own notes
 * only (managers+ could delete any, but we don't know the caller's role at
 * this point — the server enforces it and returns 403 on unauthorized
 * attempts, which we surface).
 */
export default function AdvisoryNotesPanel({ orgId, findingId }: AdvisoryNotesPanelProps) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<AdvisoryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newBody, setNewBody] = useState('');
  const [newPriority, setNewPriority] = useState<AdvisoryNotePriority | ''>('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await advisoryNoteService.list(orgId, findingId);
      setNotes(res);
    } catch (err) {
      setError(toDisplayError(err, t('advisoryNotes.errors.loadFailed', 'Failed to load notes')));
    } finally {
      setLoading(false);
    }
  }, [orgId, findingId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!newBody.trim()) return;
    setCreating(true);
    setError(null);
    try {
      await advisoryNoteService.create(orgId, findingId, {
        body: newBody.trim(),
        priority: newPriority || null,
      });
      setNewBody('');
      setNewPriority('');
      await load();
    } catch (err) {
      setError(toDisplayError(err, t('advisoryNotes.errors.createFailed', 'Failed to add note')));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (note: AdvisoryNote) => {
    try {
      await advisoryNoteService.delete(orgId, note.id);
      await load();
    } catch (err) {
      setError(
        toDisplayError(err, t('advisoryNotes.errors.deleteFailed', 'Failed to delete note'))
      );
    }
  };

  const authorDisplay = (n: AdvisoryNote): string => {
    const author = n.author;
    if (!author) return t('advisoryNotes.unknownAuthor', 'Unknown');
    const first = author.first_name || '';
    const last = author.last_name || '';
    const name = `${first} ${last}`.trim();
    return name || author.email;
  };

  return (
    <Box
      sx={{ p: 2, bgcolor: 'background.default', borderTop: '1px dashed', borderColor: 'divider' }}
    >
      <Typography variant="subtitle2" gutterBottom>
        {t('advisoryNotes.title', 'Advisory Notes')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : notes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('advisoryNotes.empty', 'No advisory notes yet. Add remediation guidance below.')}
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mb: 2 }}>
          {notes.map((n) => (
            <Box key={n.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {n.priority && (
                  <Chip size="small" label={n.priority} color={PRIORITY_COLOR[n.priority]} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {authorDisplay(n)} &middot; {formatDateTime(n.created_at)}
                </Typography>
                <Tooltip title={t('advisoryNotes.delete', 'Delete note')}>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(n)}
                    aria-label={t('advisoryNotes.delete', 'Delete note')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {n.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          multiline
          minRows={1}
          maxRows={4}
          placeholder={t('advisoryNotes.addPlaceholder', 'Add remediation guidance…')}
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          disabled={creating}
          sx={{ flex: 1 }}
          fullWidth
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id={`priority-${findingId}`}>
            {t('advisoryNotes.priority', 'Priority')}
          </InputLabel>
          <Select
            labelId={`priority-${findingId}`}
            label={t('advisoryNotes.priority', 'Priority')}
            value={newPriority}
            onChange={(e) => setNewPriority(e.target.value as AdvisoryNotePriority | '')}
            disabled={creating}
          >
            <MenuItem value="">{t('advisoryNotes.anyPriority', '—')}</MenuItem>
            <MenuItem value="critical">
              {t('advisoryNotes.priorities.critical', 'Critical')}
            </MenuItem>
            <MenuItem value="high">{t('advisoryNotes.priorities.high', 'High')}</MenuItem>
            <MenuItem value="normal">{t('advisoryNotes.priorities.normal', 'Normal')}</MenuItem>
            <MenuItem value="low">{t('advisoryNotes.priorities.low', 'Low')}</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleCreate} disabled={creating || !newBody.trim()}>
          {creating ? <CircularProgress size={20} /> : t('advisoryNotes.add', 'Add note')}
        </Button>
      </Stack>
    </Box>
  );
}
